import type { PackDetail, PackVersion } from '@planuze/admin-contracts';
import {
  Breadcrumb,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Page,
  PageContent,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@planuze/ui';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { CopyableId } from '../components/copyable-id.js';
import { DetailPageState } from '../components/detail-page-state.js';
import { SummaryMetrics } from '../components/detail-summary.js';
import {
  kindLabel,
  tierLabel,
  visibilityLabel,
} from '../components/packs-catalog/pack-catalog-labels.js';
import { PackDetailCard } from '../components/packs-catalog/pack-detail-card.js';
import { PackTakedownHistoryTab } from '../components/packs-catalog/pack-takedown-history-tab.js';
import { PackVersionsTab } from '../components/packs-catalog/pack-versions-tab.js';
import { ReasonDialog } from '../components/reason-dialog.js';
import { usePackDetailContent } from '../content/sections/pack-detail.js';
import { usePackDetail, useTakedownPackVersion } from '../hooks/use-packs-catalog.js';
import { useStaffSession } from '../hooks/use-staff-session.js';
import { fmtDateTime, fmtNumber } from '../lib/format.js';

const routeApi = getRouteApi('/authenticated/packs/$packId');

/* Detalhe do pack publicado (ADR-0703). Com o catálogo agora paginado por cursor keyset, o pack
   pode não estar numa página já carregada — então cabeçalho E conteúdo rico (metadata + versões +
   histórico de takedown) vêm do endpoint dedicado (`usePackDetail`, `GET /v1/admin/packs/:packId`),
   fonte única e sempre acessível por id direto. Texto pt-BR plano (admin-web não usa @planuze/translate). */
export function PackDetailPage() {
  const { packId } = routeApi.useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = usePackDetail(packId);
  const copy = usePackDetailContent();

  const renderHeader = (label: string): ReactNode => (
    <PageHeader>
      <PageTitleBlock>
        <Breadcrumb
          ariaLabel={copy.breadcrumbAriaLabel}
          items={[
            {
              label: copy.catalogBreadcrumb,
              onClick: () => {
                navigate({ to: '/packs' }).catch(() => undefined);
              },
            },
            { label },
          ]}
        />
      </PageTitleBlock>
    </PageHeader>
  );

  if (isError) {
    /* 404 (pack removido/inexistente) e demais falhas caem no mesmo estado de erro da tela. */
    return (
      <DetailPageState
        header={renderHeader(copy.packFallbackLabel)}
        kind="error"
        message={copy.loadError}
      />
    );
  }
  if (isLoading || data === undefined) {
    return (
      <DetailPageState header={renderHeader(copy.packFallbackLabel)} kind="loading" message="" />
    );
  }

  return <PackDetailBody pack={data} />;
}

function PackDetailBody({ pack }: { readonly pack: PackDetail }) {
  const navigate = useNavigate();
  const session = useStaffSession();
  const takedown = useTakedownPackVersion();
  const [pending, setPending] = useState<PackVersion | null>(null);
  const hasActiveVersion = pack.latestActive !== null;
  /* Takedown é destrutivo → só super_admin (espelha o RBAC do admin-service, ADR-0464). */
  const canTakedown = session !== null && session.role === 'super_admin';
  const copy = usePackDetailContent();

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <Breadcrumb
            ariaLabel={copy.breadcrumbAriaLabel}
            items={[
              {
                label: copy.catalogBreadcrumb,
                onClick: () => {
                  navigate({ to: '/packs' }).catch(() => undefined);
                },
              },
              { label: pack.displayName },
            ]}
          />
          <div className="flex flex-wrap items-center gap-2">
            <PageTitle>{pack.displayName}</PageTitle>
            {pack.verified && (
              <StatusBadge variant="success" dot>
                {copy.verifiedBadge}
              </StatusBadge>
            )}
          </div>
          <CopyableId value={pack.id} label={copy.packIdLabel} />
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <StatusBadge variant="info">{visibilityLabel(pack.visibility)}</StatusBadge>
            <StatusBadge variant="neutral">{tierLabel(pack.tier)}</StatusBadge>
            <StatusBadge variant="neutral">{kindLabel(pack.kind)}</StatusBadge>
            {!hasActiveVersion && (
              <StatusBadge variant="warning">{copy.noActiveVersionBadge}</StatusBadge>
            )}
          </div>
        </PageTitleBlock>
      </PageHeader>
      <PageContent className="space-y-6">
        <SummaryMetrics
          items={[
            { label: copy.metrics.installs, value: fmtNumber(pack.installs) },
            {
              label: copy.metrics.activeVersion,
              value: hasActiveVersion ? `v${pack.latestActive}` : '—',
              ...(hasActiveVersion ? {} : { hint: copy.metrics.allYankedHint }),
            },
            { label: copy.metrics.versions, value: pack.versionCount },
          ]}
        />
        {/* Sub-follow-up: uma aba "Analytics" com série temporal de instalações exige um endpoint
            de staff por pack. A série existente (`/v1/publisher-analytics`) é por-publisher e
            IDOR-gated ao dono — não há série de installs por-pack acessível ao backoffice. Não
            criar a aba sem esse backend. */}
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">{copy.tabs.overview}</TabsTrigger>
            <TabsTrigger value="versions">{copy.tabs.versions}</TabsTrigger>
            <TabsTrigger value="takedowns">{copy.tabs.takedowns}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{copy.overviewCardTitle}</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid gap-4 sm:grid-cols-2">
                  <Field label={copy.fields.author}>{pack.author}</Field>
                  <Field label={copy.fields.kind}>{kindLabel(pack.kind)}</Field>
                  <Field label={copy.fields.tier}>{tierLabel(pack.tier)}</Field>
                  <Field label={copy.fields.visibility}>{visibilityLabel(pack.visibility)}</Field>
                  <Field label={copy.fields.verified}>
                    {pack.verified ? (
                      <StatusBadge variant="success" dot>
                        {copy.verifiedBadge}
                      </StatusBadge>
                    ) : (
                      <StatusBadge variant="neutral">{copy.notVerifiedBadge}</StatusBadge>
                    )}
                  </Field>
                  <Field label={copy.fields.updatedAt}>{fmtDateTime(pack.lastUpdated)}</Field>
                </dl>
              </CardContent>
            </Card>
            <PackDetailCard detail={pack} />
            {pack.visibility === 'org-private' && (
              <Card>
                <CardHeader>
                  <CardTitle>{copy.allowedOrgsCardTitle}</CardTitle>
                </CardHeader>
                <CardContent>
                  {pack.allowedOrgs !== undefined && pack.allowedOrgs.length > 0 ? (
                    <ul className="flex flex-wrap gap-2">
                      {pack.allowedOrgs.map((orgId) => (
                        <li key={orgId}>
                          <span className="rounded-md border border-border bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground">
                            {orgId}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">{copy.noAllowedOrgs}</p>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="versions">
            <PackVersionsTab
              versions={pack.versions}
              canTakedown={canTakedown}
              pendingVersion={pending?.version ?? null}
              isTakingDown={takedown.isPending}
              onTakedown={setPending}
            />
          </TabsContent>

          <TabsContent value="takedowns">
            <PackTakedownHistoryTab versions={pack.versions} />
          </TabsContent>
        </Tabs>
      </PageContent>

      {pending !== null && (
        <ReasonDialog
          open
          onClose={() => setPending(null)}
          title={copy.takedownDialog.title(pack.displayName, pending.version)}
          description={copy.takedownDialog.description}
          confirmLabel={copy.takedownDialog.confirmLabel}
          reasonLabel={copy.takedownDialog.reasonLabel}
          destructive
          pending={takedown.isPending}
          validate={(reason) => reason.trim().length > 0}
          onConfirm={(reason) => {
            takedown.mutate(
              { packId: pack.id, version: pending.version, reason },
              {
                onSuccess: () => {
                  toast.success(copy.takedownDialog.successToast);
                  setPending(null);
                },
                onError: () => toast.error(copy.takedownDialog.errorToast),
              },
            );
          }}
        />
      )}
    </Page>
  );
}

function Field({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}
